/**
 * Portfolio (docs/portfolio-ux.md) — replaces the old Positions page. Only orders placed through
 * OpenMarkets (`GET /auth/orders`), in one currency at a time (Real · USD / Paper · ATLAS, which
 * follows the global trading mode and switches freely):
 *
 *   summary   At risk · Value now (est.) · Could win · Working
 *   Working   resting orders: fill bar, price, wait, venue, Cancel (user click only)
 *   games     one soft card per game held — live first, then by start — with a row per side +
 *             venue: contracts, average price, price now ▲/▼, and Add more / Hedge / Game page /
 *             Order details. Add more and Hedge only OPEN the trade ticket (drawer on desktop,
 *             bottom sheet on mobile, the same as Markets); nothing here places an order.
 *   settled   the last 7 days' results, then "All history →"
 *   plays     Revision (docs/portfolio-ux.md): orders sharing an opportunity with 2+ legs (an arb,
 *             a market making quote, a hedged bet) render as ONE block inside their game card —
 *             kind chip, the payout per outcome, the legs underneath. Exposed plays (one side
 *             filled, the other not) are red, and an "Exposed (N)" chip filters to their games.
 *             Working orders in a play carry its chip.
 *
 * The screen reads everything through a `PortfolioSource` so the dev preview (/dev/portfolio)
 * can render it from mock data; `Portfolio` below wires the live hooks.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import type {
  Contest,
  League,
  LiquidityPosition,
  Opportunity,
  Order,
  OrderResolution,
  PartnerLiquidity,
  RestingOrder,
} from "@greenbook/client";
import {
  useAccountPartners,
  useCancelOrders,
  useContestStateBatch,
  useLeagues,
  useOpenPlayOpportunities,
  useOrders,
  usePortfolioLiquidity,
  useResolution,
  useRestingOrders,
} from "../lib/queries.js";
import { apiGet } from "../lib/api.js";
import { setTradingMode, useTradingMode, useVenueStatus } from "../lib/venues.js";
import { useLiveContest, useLiveEnabled, useOrderPulse } from "../lib/ws.js";
import { useOddsFormat } from "../lib/odds.js";
import { useIsMobile } from "../lib/responsive.js";
import { fmtAge, fmtDateTime, fmtKickoff, fmtMoney, fmtPrice, fmtRelative } from "../lib/format.js";
import { sideColumnLabel } from "../lib/books.js";
import { isGame, matchupSep, startMs, teamLabel, teamsOf } from "../lib/marketsBoard.js";
import {
  buildHoldings,
  combineValues,
  fillPct,
  findComplement,
  fmtWaited,
  groupSettled,
  hedgeStake,
  isLiveGame,
  liveFromState,
  NO_CONTEST,
  pendingValue,
  reconcilePresence,
  recentSettled,
  rowPrice,
  rowsValue,
  settledFromDate,
  settlePresence,
  sortGames,
  waitedSeconds,
  waitingAmt,
  type Ccy,
  type GameHolding,
  type HoldingRow,
  type OrderLike,
  type PresenceEntry,
  type RowPrice,
  type SettledGame,
  type ValueSummary,
} from "../lib/portfolio.js";
import { marketHref } from "../lib/marketView.js";
import { mmFocusHref } from "../lib/marketMaking.js";
import { useMmQuoteIds } from "../lib/mmModel.js";
import {
  collectPlays,
  exposedPlays,
  playsFromOpportunities,
  legState,
  outcomeLabel,
  PLAY_LABEL,
  type Play,
  type PlayLegLike,
} from "../lib/plays.js";
import { ErrorState } from "../components/ui.js";
import { FlashText, Pill, SectionHeading, Skeleton, SoftCard, TicketDrawer } from "../components/kit.js";
import { VenueLogo } from "../components/VenueLogo.js";
import { TradeTicket } from "../components/TradeTicket.js";
import { ResolutionDetail as ResolutionView } from "../components/ResolutionDetail.js";

/* =================================================================== source */

export interface ListState<T> {
  items: T[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMore?: () => void;
}

export type StateEntry = {
  live?: boolean;
  state?: {
    game_status?: string | null;
    clock?: { display?: string | null } | null;
    period?: { label?: string | null } | null;
    scores?: { home?: number | null; away?: number | null; home_score?: number | null; away_score?: number | null } | null;
  } | null;
};

/**
 * The opportunity a hedge joins: a row can merge several orders (same side and venue), each with
 * its own opportunity, and a buy takes one, so the most recent order's. With its model: an
 * opportunity belongs to one model, and adding to it must send that same model (or none when it
 * has none), else the API answers `400 model_mismatch`.
 */
function latestOpportunity(
  orders: readonly { opportunity_id?: string | null; model_id?: string | null; created_at?: string | null }[],
): { opportunityId?: string; modelId?: string } {
  let best: { id: string; model: string | null; at: string } | null = null;
  for (const o of orders) {
    if (!o.opportunity_id) continue;
    const at = o.created_at ?? "";
    if (!best || at > best.at) best = { id: o.opportunity_id, model: o.model_id ?? null, at };
  }
  if (!best) return {};
  // Any order of that opportunity that names a model names the opportunity's model.
  const model = best.model ?? orders.find((o) => o.opportunity_id === best!.id && o.model_id)?.model_id ?? null;
  return model ? { opportunityId: best.id, modelId: model } : { opportunityId: best.id };
}

/** What the ticket drawer is asked to open. */
export interface TicketPick {
  key: string;
  kind: "add" | "hedge";
  contestId: string;
  position: LiquidityPosition;
  pl: PartnerLiquidity;
  initialStake?: number;
  hint?: string;
  /** Hedge: the hedged order's opportunity, so the pair groups as one play. */
  opportunityId?: string;
  /** Hedge: that opportunity's model, when it has one (the hedge must carry the same model). */
  modelId?: string;
}

/**
 * Everything the screen reads. Each `use*` is a hook, called unconditionally in the same order
 * on every render; the object itself must be stable for the screen's lifetime.
 */
export interface PortfolioSource {
  useOpen(ccy: Ccy): ListState<Order>;
  useResting(): ListState<RestingOrder> & { fetchedAt: number; live: boolean };
  useSettled(ccy: Ccy, from: string): ListState<Order>;
  useContests(ids: string[]): (id: string) => Contest | undefined;
  useLeagues(): (id: string | undefined) => League | undefined;
  useStates(ids: string[]): (id: string) => StateEntry | undefined;
  /** Venue scope for "best price": Real → connected venues (null = any); Paper → null. */
  useScope(ccy: Ccy): { allowed: ReadonlySet<string> | null; connectedVenues: number; venuesLoading: boolean };
  useGameLiquidity(
    contestId: string,
    game: boolean,
    hashes: string[],
    opts: { enabled: boolean; poll: boolean },
  ): { data?: { positions: LiquidityPosition[] }; isError: boolean; refetch: () => void };
  /** Stream these contests' prices; returns whether the stream is live. */
  useLiveGames(ids: string[]): boolean;
  useCancel(): { cancel: (id: string) => void; pendingId: string | null; error: string | null };
  useResolution(id: string, enabled: boolean): { data?: OrderResolution; isLoading: boolean; isError: boolean; error: unknown };
  useOrderPulse(): Set<string>;
  /** Open opportunities with their full legs (cancelled ones too), for plays and exposure. */
  useOpenPlays(): { items: Opportunity[]; isLoading: boolean };
  /** Opportunity ids greenbook's Market making screen created (its registry). */
  useMmIds(): ReadonlySet<string>;
  renderTicket(pick: TicketPick, onClose: () => void): ReactNode;
}

/* ------------------------------------------------------------- live source */

/** Free plan: 25 streamed contests across connections (docs → Rate limits), same cap as Markets. */
const STREAM_CAP = 25;
/** Open orders: 200 a page, pull up to this many pages up front (live book: ~60 rows). */
const OPEN_PAGES = 5;
const MIN = 60_000;

function useAutoPages(q: ReturnType<typeof useOrders>, max: number) {
  const pages = q.data?.pages.length ?? 0;
  const { hasNextPage, isFetching, isError, fetchNextPage } = q;
  useEffect(() => {
    if (hasNextPage && !isFetching && !isError && pages > 0 && pages < max) void fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetching, isError, pages, max, fetchNextPage]);
}

function listOf(q: ReturnType<typeof useOrders>, items: Order[]): ListState<Order> {
  return {
    items,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: () => void q.refetch(),
    hasMore: !!q.hasNextPage,
    loadingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
  };
}

const NO_OPPS: Opportunity[] = [];

/** The live hooks behind Portfolio (also read by the Desk's In play glance). */
export const liveSource: PortfolioSource = {
  useOpen(ccy) {
    const q = useOrders(200, { status: "open", currency: ccy });
    useAutoPages(q, OPEN_PAGES);
    const items = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data]);
    return listOf(q, items);
  },
  useResting() {
    const q = useRestingOrders();
    const live = useLiveEnabled();
    return {
      items: q.data?.orders ?? [],
      isLoading: q.isLoading,
      isError: q.isError,
      error: q.error,
      refetch: () => void q.refetch(),
      fetchedAt: q.dataUpdatedAt || Date.now(),
      live,
    };
  },
  useSettled(ccy, from) {
    const q = useOrders(200, { settled: true, sort: "settled", from, currency: ccy });
    const items = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data]);
    return listOf(q, items);
  },
  useContests(ids) {
    const results = useQueries({
      queries: ids.map((id) => ({
        queryKey: ["contest", id],
        queryFn: async ({ signal }: { signal: AbortSignal }) => (await apiGet<Contest>(`/contests/${encodeURIComponent(id)}`, signal)).data,
        staleTime: 5 * MIN,
      })),
    });
    // useQueries hands back a new array every render; key the lookup on what actually changed.
    const sig = ids.map((id, i) => `${id}:${results[i]?.dataUpdatedAt ?? 0}`).join(",");
    const map = useMemo(() => {
      const m = new Map<string, Contest>();
      ids.forEach((id, i) => {
        const d = results[i]?.data;
        if (d) m.set(id, d);
      });
      return m;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig]);
    return useCallback((id: string) => map.get(id), [map]);
  },
  useLeagues() {
    const q = useLeagues();
    const map = useMemo(() => new Map((q.data ?? []).map((l) => [l.id, l])), [q.data]);
    return useCallback((id: string | undefined) => (id ? map.get(id) : undefined), [map]);
  },
  useStates(ids) {
    const q = useContestStateBatch(ids.slice(0, 100));
    return useCallback((id: string) => q.data?.[id] as StateEntry | undefined, [q.data]);
  },
  useScope(ccy) {
    const partnersQ = useAccountPartners();
    const venues = useVenueStatus();
    const allowed = useMemo(() => {
      if (ccy !== "USD") return null;
      const s = new Set<string>();
      for (const ap of partnersQ.data?.account_partners ?? []) {
        if (ap.status === "active" && ap.credential_status === "connected") s.add(ap.partner_id);
      }
      return s.size > 0 ? s : null;
    }, [ccy, partnersQ.data]);
    return { allowed, connectedVenues: venues.connectedCount, venuesLoading: venues.loading };
  },
  useGameLiquidity(contestId, game, hashes, opts) {
    const q = usePortfolioLiquidity(contestId, game, hashes, opts);
    return { data: q.data, isError: q.isError, refetch: () => void q.refetch() };
  },
  useLiveGames(ids) {
    useLiveContest(ids);
    return useLiveEnabled();
  },
  useCancel() {
    const m = useCancelOrders();
    const [pendingId, setPendingId] = useState<string | null>(null);
    const perIdErrors = m.data?.data.errors ?? [];
    const error = m.isError
      ? `Cancel failed${m.error && "code" in m.error ? ` · ${(m.error as { code?: string }).code}` : ""}`
      : perIdErrors.length > 0
        ? perIdErrors.map((e) => e.error).join(" · ")
        : null;
    return {
      cancel: (id: string) => {
        setPendingId(id);
        m.mutate([id], { onSettled: () => setPendingId(null) });
      },
      pendingId: m.isPending ? pendingId : null,
      error,
    };
  },
  useResolution(id, enabled) {
    const q = useResolution(id, enabled);
    return { data: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error };
  },
  useOrderPulse() {
    return useOrderPulse();
  },
  useOpenPlays() {
    const q = useOpenPlayOpportunities();
    return { items: q.data ?? NO_OPPS, isLoading: q.isLoading };
  },
  useMmIds() {
    return useMmQuoteIds();
  },
  renderTicket(pick, onClose) {
    return (
      <TradeTicket
        key={pick.key}
        position={pick.position}
        pl={pick.pl}
        initialStake={pick.initialStake}
        opportunityId={pick.opportunityId}
        modelId={pick.modelId}
        onClose={onClose}
      />
    );
  },
};

/** `/portfolio?filter=exposed` — the nav badge's and the Desk's link to the exposed plays. */
export const PORTFOLIO_EXPOSED_HREF = "/portfolio?filter=exposed";

/** The routed page (`/portfolio`; `/orders` and `/opportunities` redirect here). */
export function Portfolio() {
  // `/portfolio#working` (the Desk's "Working orders" item): open on the real book, where
  // resting orders live, and bring the Working strip into view.
  const { hash, search } = useLocation();
  const exposed = new URLSearchParams(search).get("filter") === "exposed";
  return <PortfolioScreen key={exposed ? "exposed" : "all"} source={liveSource} focusWorking={hash === "#working"} initialExposed={exposed} />;
}

/* =================================================================== helpers */

const money = (n: number, ccy: Ccy): string =>
  ccy === "ATLAS" ? `${Math.round(n).toLocaleString()} ATLAS` : fmtMoney(n);
const signedMoney = (n: number, ccy: Ccy): string => {
  const sign = n > 0.005 ? "+" : n < -0.005 ? "−" : "";
  return `${sign}${money(Math.abs(n), ccy)}`;
};
const toneOf = (n: number): string => (n > 0.005 ? "text-up" : n < -0.005 ? "text-down" : "text-dim");
const arrowOf = (n: number): string => (n > 0.005 ? "▲" : n < -0.005 ? "▼" : "");

/** Resting rows carry no currency; paper never rests, so they're real money (USD). */
function orderCcy(o: unknown): string {
  const r = o as { currency?: string | null; funding?: { currency?: string | null } };
  return (r.currency ?? r.funding?.currency ?? "USD").toUpperCase();
}

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Animate items in and out of a list (enter: slide in; exit: fade out), after the first load. */
function usePresence<T>(items: T[], keyOf: (t: T) => string, ready: boolean): PresenceEntry<T>[] {
  const [entries, setEntries] = useState<PresenceEntry<T>[]>(() => reconcilePresence([], items, keyOf, false));
  const primed = useRef(false);
  const keyRef = useRef(keyOf);
  keyRef.current = keyOf;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // Re-reconcile only when the SET of keys changes. Keying the effect on `items` itself looped
  // forever ("Maximum update depth exceeded", a black page on reload) whenever a caller's list
  // was rebuilt each render, as the live data source's lists are.
  const sig = items.map(keyOf).join("\u0001");
  useLayoutEffect(() => {
    const animate = primed.current && !prefersReducedMotion();
    setEntries((prev) => reconcilePresence(prev, itemsRef.current, keyRef.current, animate));
    if (ready) primed.current = true;
  }, [sig, ready]);
  useEffect(() => {
    if (!entries.some((e) => e.phase !== "stay")) return;
    const t = setTimeout(() => setEntries(settlePresence), 260);
    return () => clearTimeout(t);
  }, [entries]);
  // Always render the CURRENT item for each key (prices, fills), not the copy captured when the
  // key first appeared; exiting entries keep their last copy.
  const byKey = new Map(items.map((it) => [keyOf(it), it] as const));
  return entries.map((e) => {
    const cur = byKey.get(e.key);
    return cur !== undefined && cur !== e.item ? { ...e, item: cur } : e;
  });
}
const phaseClass = (p: PresenceEntry<unknown>["phase"]): string => (p === "enter" ? "gb-row-in" : p === "exit" ? "gb-card-out" : "");

/** Near the viewport (once, for lazy prices) and on screen (for polling / streaming). */
function useNearViewport(ref: RefObject<Element>, root: Element | null): { seen: boolean; visible: boolean } {
  const [seen, setSeen] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !root || typeof IntersectionObserver === "undefined") return;
    const near = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setSeen(true), {
      root,
      rootMargin: "400px 0px 400px 0px",
    });
    const vis = new IntersectionObserver((es) => {
      for (const e of es) setVisible(e.isIntersecting);
    }, { root });
    near.observe(el);
    vis.observe(el);
    return () => {
      near.disconnect();
      vis.disconnect();
      setVisible(false);
    };
  }, [ref, root]);
  return { seen, visible };
}

function scoresOf(st: StateEntry | undefined): { home: number | null; away: number | null } | null {
  const s = st?.state?.scores;
  if (!s) return null;
  const home = s.home ?? s.home_score ?? null;
  const away = s.away ?? s.away_score ?? null;
  return home == null && away == null ? null : { home, away };
}

function matchupOf(c: Contest | undefined, fallback: string): string {
  if (!c) return fallback;
  const t = teamsOf(c);
  return t ? `${teamLabel(t.away)} ${matchupSep(c)} ${teamLabel(t.home)}` : (c.title ?? fallback);
}

/** How the opposite side reads in the hedge hint: "Celtics −4.5", "Under 50.5", "Not Draw". */
function oppositeLabel(p: LiquidityPosition): string {
  const side = (p.side_key ?? "").toLowerCase();
  if (side === "yes" || side === "no") return p.title ?? sideColumnLabel(p, side);
  return sideColumnLabel(p, side);
}

/* =================================================================== screen */

export function PortfolioScreen({
  source,
  nowOverride,
  initialCcy,
  focusWorking = false,
  initialExposed = false,
}: {
  source: PortfolioSource;
  nowOverride?: number;
  /** Dev preview: start on this book instead of following the trading mode. */
  initialCcy?: Ccy;
  /** Open on the real book (resting orders are real money) and scroll to the Working strip. */
  focusWorking?: boolean;
  /** Open with the Exposed filter on (`?filter=exposed`). */
  initialExposed?: boolean;
}) {
  // Kept across book switches, so "Exposed" stays on when you hop to the other book.
  const [onlyExposed, setOnlyExposed] = useState(initialExposed);
  const mode = useTradingMode();
  const [ccy, setCcy] = useState<Ccy>(initialCcy ?? (focusWorking ? "USD" : mode === "paper" ? "ATLAS" : "USD"));
  // Follows the global trading mode (paper fills are booked in ATLAS); the pills switch freely.
  const follow = initialCcy == null;
  // With focusWorking the first (mount) sync is skipped so the real book stays; later mode
  // changes are followed as usual.
  const skipSync = useRef(focusWorking);
  useEffect(() => {
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    if (follow) setCcy(mode === "paper" ? "ATLAS" : "USD");
  }, [mode, follow]);
  // Remount per currency so switching never animates one book into the other.
  return (
    <PortfolioBody
      key={ccy}
      source={source}
      ccy={ccy}
      onCcy={setCcy}
      nowOverride={nowOverride}
      focusWorking={focusWorking && ccy === "USD"}
      onlyExposed={onlyExposed}
      onExposed={setOnlyExposed}
    />
  );
}

function PortfolioBody({
  source,
  ccy,
  onCcy,
  nowOverride,
  focusWorking = false,
  onlyExposed,
  onExposed,
}: {
  source: PortfolioSource;
  ccy: Ccy;
  onCcy: (c: Ccy) => void;
  nowOverride?: number;
  focusWorking?: boolean;
  onlyExposed: boolean;
  onExposed: (v: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const mode = useTradingMode();

  // Ticker: "waited 12m", live/soon, the 7-day window.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  const now = nowOverride ?? tick;

  const open = source.useOpen(ccy);
  const resting = source.useResting();
  const fromDay = settledFromDate(now);
  const settled = source.useSettled(ccy, fromDay);
  const scope = source.useScope(ccy);
  const leagueOf = source.useLeagues();
  const pulse = source.useOrderPulse();
  const cancel = source.useCancel();

  const openPlays = source.useOpenPlays();
  const mmIds = source.useMmIds();

  const working = useMemo(() => resting.items.filter((o) => orderCcy(o) === ccy), [resting.items, ccy]);
  // Plays: open orders sharing an opportunity with 2+ legs, plus that opportunity's cancelled legs.
  const plays = useMemo(() => collectPlays(open.items, openPlays.items, { mmIds }), [open.items, openPlays.items, mmIds]);
  const playOfLeg = useMemo(() => {
    const m = new Map<string, Play>();
    for (const p of plays.values()) for (const l of p.legs) m.set(l.router_order_id, p);
    return m;
  }, [plays]);
  const holdings = useMemo(
    () => buildHoldings(open.items, (o) => {
      const id = (o as PlayLegLike).opportunity_id;
      return id && plays.has(id) ? id : null;
    }),
    [open.items, plays],
  );
  const exposed = useMemo(() => exposedPlays(plays.values()), [plays]);
  const exposedGames = useMemo(() => new Set(exposed.map((p) => p.contestId ?? NO_CONTEST)), [exposed]);
  // The other book's exposed plays (from the open opportunities), so the filter can point there.
  const otherCcy: Ccy = ccy === "USD" ? "ATLAS" : "USD";
  const otherExposed = useMemo(
    () => exposedPlays(playsFromOpportunities(openPlays.items, { mmIds })).filter((p) => p.ccy === otherCcy).length,
    [openPlays.items, mmIds, otherCcy],
  );
  const filterOn = onlyExposed && exposed.length > 0;
  const settledGames = useMemo(() => groupSettled(recentSettled(settled.items, now)), [settled.items, now]);

  // Settled games shown (a page at a time) — their contest details load with the held ones.
  const SETTLED_PAGE = 6;
  const [settledShown, setSettledShown] = useState(SETTLED_PAGE);
  const shownSettled = useMemo(() => settledGames.slice(0, settledShown), [settledGames, settledShown]);

  const heldIds = useMemo(() => holdings.map((g) => g.contestId).filter((id) => id !== NO_CONTEST), [holdings]);
  const metaIds = useMemo(
    () => [...new Set([...heldIds, ...shownSettled.map((g) => g.contestId).filter((id) => id !== NO_CONTEST)])],
    [heldIds, shownSettled],
  );
  const contestOf = source.useContests(metaIds);
  const stateOf = source.useStates(heldIds);
  const liveOf = useCallback((id: string) => liveFromState(stateOf(id)), [stateOf]);
  const allGames = useMemo(() => sortGames(holdings, contestOf, liveOf, now), [holdings, contestOf, liveOf, now]);
  const games = useMemo(() => (filterOn ? allGames.filter((g) => exposedGames.has(g.contestId)) : allGames), [allGames, filterOn, exposedGames]);

  // Value now: each game card reports its rows' prices once its liquidity lands.
  const [values, setValues] = useState<Map<string, ValueSummary>>(() => new Map());
  const onValue = useCallback((id: string, v: ValueSummary) => {
    setValues((m) => {
      const o = m.get(id);
      if (o && o.value === v.value && o.cost === v.cost && o.priced === v.priced && o.total === v.total && o.pending === v.pending) return m;
      return new Map(m).set(id, v);
    });
  }, []);
  const valueTotal = useMemo(
    () => combineValues(allGames.map((g) => values.get(g.contestId) ?? pendingValue(g.rows.length))),
    [allGames, values],
  );

  // Streaming: the on-screen games, capped like Markets.
  const visibleSet = useRef(new Set<string>());
  const [visibleTick, setVisibleTick] = useState(0);
  const onVisible = useCallback((id: string, v: boolean) => {
    const s = visibleSet.current;
    if (v === s.has(id)) return;
    if (v) s.add(id);
    else s.delete(id);
    setVisibleTick((t) => t + 1);
  }, []);
  const streamIds = useMemo(
    () => games.map((g) => g.contestId).filter((id) => visibleSet.current.has(id)).slice(0, STREAM_CAP),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [games, visibleTick],
  );
  const liveStream = source.useLiveGames(streamIds);
  const streamed = useMemo(() => new Set(liveStream ? streamIds : []), [liveStream, streamIds]);

  // Ticket drawer.
  const [ticket, setTicket] = useState<TicketPick | null>(null);
  const closeTicket = useCallback(() => setTicket(null), []);

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const workingRef = useRef<HTMLDivElement>(null);
  const scrollToWorking = () => workingRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });

  const ready = !open.isLoading && !resting.isLoading;
  // Arrived from the Desk's Working orders item: bring the strip into view once, when it's there.
  const focused = useRef(false);
  useEffect(() => {
    if (!focusWorking || focused.current || !ready || working.length === 0) return;
    focused.current = true;
    requestAnimationFrame(() => workingRef.current?.scrollIntoView({ block: "start" }));
  }, [focusWorking, ready, working.length]);
  const gameEntries = usePresence(games, (g) => g.contestId, ready);
  const workingEntries = usePresence(working, (o) => o.router_order_id, ready);
  const settledEntries = usePresence(shownSettled, (g) => g.contestId, !settled.isLoading);

  const atRisk = holdings.reduce((n, g) => n + g.stake, 0);
  const couldWin = holdings.reduce((n, g) => n + g.payout, 0);
  const openCount = holdings.reduce((n, g) => n + g.rows.length, 0);
  const waiting = working.reduce((n, o) => n + waitingAmt(o), 0);
  const empty = ready && !open.isError && holdings.length === 0 && working.length === 0;

  const ticketMismatch = ticket ? (ccy === "ATLAS" ? mode === "real" : mode === "paper") : false;
  const ticketPanel = ticket && (
    <div className="flex min-h-0 flex-1 flex-col">
      {(ticket.hint || ticketMismatch) && (
        <div className="shrink-0 space-y-2 border-b border-border bg-panel-2 px-3 py-2.5 text-[12px] leading-snug">
          {ticket.hint && <div className="text-ink">{ticket.hint}</div>}
          {ticketMismatch && (
            <div className="flex flex-wrap items-center gap-2 text-amber">
              <span className="min-w-0 flex-1">
                {ccy === "ATLAS"
                  ? "The ticket follows your trading mode, which is Real: this would be a real-money order, not paper."
                  : "The ticket follows your trading mode, which is Paper: this adds a paper position, not to this real one."}
              </span>
              <button
                type="button"
                onClick={() => setTradingMode(ccy === "ATLAS" ? "paper" : "real")}
                className="min-h-9 shrink-0 rounded-full border border-amber/60 bg-amber/15 px-3 text-[12px] font-semibold text-amber hover:bg-amber/25"
              >
                Switch to {ccy === "ATLAS" ? "Paper" : "Real"}
              </button>
            </div>
          )}
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{source.renderTicket(ticket, closeTicket)}</div>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1">
        <div ref={setScrollEl} className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto flex max-w-5xl flex-col gap-3 p-4">
            {/* currency switch */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2" role="group" aria-label="Book">
                <Pill active={ccy === "USD"} onClick={() => onCcy("USD")}>
                  Real · USD
                </Pill>
                <Pill active={ccy === "ATLAS"} onClick={() => onCcy("ATLAS")}>
                  Paper · ATLAS
                </Pill>
              </div>
              {exposed.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onExposed(!filterOn)}
                  aria-pressed={filterOn}
                  title="Plays with one side filled and the other not: you're betting on one outcome"
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-semibold transition-colors duration-150 motion-reduce:transition-none lg:min-h-8 ${
                    filterOn ? "border-down/70 bg-down/20 text-down" : "border-down/40 bg-down/10 text-down hover:bg-down/15"
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-down" aria-hidden />
                  Exposed ({exposed.length})
                  {filterOn && <span aria-hidden>✕</span>}
                </button>
              ) : onlyExposed && otherExposed > 0 ? (
                <button
                  type="button"
                  onClick={() => onCcy(otherCcy)}
                  className="inline-flex min-h-11 items-center rounded-full border border-down/40 bg-down/10 px-3.5 text-[13px] font-semibold text-down hover:bg-down/15 lg:min-h-8"
                >
                  {otherExposed} exposed in {otherCcy === "ATLAS" ? "Paper" : "Real"} →
                </button>
              ) : null}
              <span className="ml-auto text-[11px] text-faint">Orders placed through OpenMarkets</span>
            </div>

            {open.isError ? (
              <ErrorState error={open.error} onRetry={open.refetch} />
            ) : empty ? (
              <EmptyNudge ccy={ccy} noVenue={ccy === "USD" && !scope.venuesLoading && scope.connectedVenues === 0} />
            ) : (
              <>
                <SummaryCards
                  loading={!ready}
                  ccy={ccy}
                  atRisk={atRisk}
                  openCount={openCount}
                  couldWin={couldWin}
                  value={valueTotal}
                  workingCount={working.length}
                  waiting={waiting}
                  onWorking={working.length > 0 ? scrollToWorking : undefined}
                />

                {(working.length > 0 || workingEntries.length > 0) && (
                  <div ref={workingRef} className="scroll-mt-4">
                    <WorkingStrip
                      entries={workingEntries}
                      now={now}
                      fetchedAt={resting.fetchedAt}
                      live={resting.live}
                      pulse={pulse}
                      cancel={cancel}
                      ccy={ccy}
                      playOf={playOfLeg}
                    />
                  </div>
                )}

                <SectionHeading
                  title={filterOn ? "Exposed" : "Open positions"}
                  count={games.length}
                  className="!pt-2"
                  right={filterOn ? (
                    <button type="button" onClick={() => onExposed(false)} className="inline-flex min-h-11 items-center text-[12px] text-dim hover:text-amber lg:min-h-0">
                      Show all {allGames.length}
                    </button>
                  ) : undefined}
                />
                {!ready ? (
                  <GamesSkeleton />
                ) : games.length === 0 && gameEntries.length === 0 ? (
                  <div className="rounded-lg border border-border bg-panel-2 p-4 text-[13px] text-dim">
                    Nothing filled yet — your working orders are above.
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {gameEntries.map((e) => (
                      <div key={e.key} className={phaseClass(e.phase)}>
                        <GameCard
                          source={source}
                          game={e.item}
                          contest={contestOf(e.item.contestId)}
                          leagueOf={leagueOf}
                          state={stateOf(e.item.contestId)}
                          now={now}
                          ccy={ccy}
                          allowed={scope.allowed}
                          root={scrollEl}
                          streamed={streamed.has(e.item.contestId)}
                          isMobile={isMobile}
                          activeKey={ticket?.key ?? null}
                          plays={plays}
                          onVisible={onVisible}
                          onValue={onValue}
                          onPick={setTicket}
                        />
                      </div>
                    ))}
                    {open.loadingMore && <div className="text-center text-[11px] text-faint">Loading more positions…</div>}
                  </div>
                )}
              </>
            )}

            <RecentlySettled
              entries={settledEntries}
              state={settled}
              ccy={ccy}
              contestOf={contestOf}
              moreLeft={settledGames.length > settledShown}
              onMore={() => {
                setSettledShown((n) => n + SETTLED_PAGE);
                if (settledGames.length <= settledShown + SETTLED_PAGE && settled.hasMore) settled.loadMore?.();
              }}
            />
          </div>
        </div>

        {/* The ticket: the same drawer as Markets (push drawer on desktop, bottom sheet on mobile). */}
        <TicketDrawer
          open={!!ticket}
          onClose={closeTicket}
          mobile={isMobile}
          header={<span className="text-[11px] font-semibold uppercase tracking-wider text-dim">{ticket?.kind === "hedge" ? "Hedge" : "Add more"}</span>}
        >
          {ticketPanel}
        </TicketDrawer>
      </div>
    </div>
  );
}

/* ================================================================== summary */

function SummaryCards({
  loading,
  ccy,
  atRisk,
  openCount,
  couldWin,
  value,
  workingCount,
  waiting,
  onWorking,
}: {
  loading: boolean;
  ccy: Ccy;
  atRisk: number;
  openCount: number;
  couldWin: number;
  value: ReturnType<typeof combineValues>;
  workingCount: number;
  waiting: number;
  onWorking?: () => void;
}) {
  const [explain, setExplain] = useState(false);
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border bg-panel-2 p-3 lg:p-4">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-2 h-6 w-24 max-w-full" />
            <Skeleton className="mt-2 h-3 w-20" />
          </div>
        ))}
      </div>
    );
  }
  const estTip =
    "An estimate: each side's contracts × its current best price (your connected venues in Real; every venue in Paper). It's what buying the same contracts would cost now, not a guaranteed sale price, and it ignores fees.";
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Stat label="At risk" value={money(atRisk, ccy)} sub={`${openCount} open`} />
      <div className="min-w-0 rounded-lg border border-border bg-panel-2 p-3 lg:p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-faint">
          Value now
          <button
            type="button"
            title={estTip}
            aria-expanded={explain}
            aria-label="What is this estimate?"
            onClick={() => setExplain((v) => !v)}
            className="-my-3 -mr-2 inline-flex min-h-11 items-center px-2 normal-case tracking-normal text-faint underline decoration-dotted underline-offset-2 hover:text-ink lg:min-h-0"
          >
            est.
          </button>
        </div>
        {value.priced === 0 && value.total > 0 ? (
          <>
            <Skeleton className="mt-1.5 h-6 w-24 max-w-full" />
            <div className="mt-1.5 text-[11px] text-faint">Loading prices…</div>
          </>
        ) : (
          <>
            <div className="nums mt-1 text-xl font-semibold leading-tight text-ink lg:text-2xl">
              <FlashText value={value.value}>{money(value.value, ccy)}</FlashText>
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-[11px]">
              {value.total > 0 && (
                <span className={`nums ${toneOf(value.change)}`}>
                  {arrowOf(value.change)} {signedMoney(value.change, ccy)}
                </span>
              )}
              <span className="text-faint" title={value.partial ? "Rows with no venue quoting them now (e.g. a finished season, or a game awaiting settlement) aren't valued" : undefined}>
                {!value.partial
                  ? "vs cost"
                  : value.pending > 0
                    ? `${value.priced} of ${value.total} priced · loading`
                    : `${value.priced} of ${value.total} priced · ${value.total - value.priced} not quoted`}
              </span>
            </div>
          </>
        )}
        {explain && <p className="mt-2 text-[11px] leading-snug text-dim">{estTip}</p>}
      </div>
      <Stat label="Could win" value={money(couldWin, ccy)} sub="if all win" tone="up" />
      <button
        type="button"
        onClick={onWorking}
        disabled={!onWorking}
        className="min-w-0 rounded-lg border border-border bg-panel-2 p-3 text-left transition-colors duration-150 enabled:hover:border-border-2 motion-reduce:transition-none lg:p-4"
        aria-label={onWorking ? "Jump to working orders" : "No working orders"}
      >
        <div className="text-[11px] uppercase tracking-wide text-faint">Working</div>
        {workingCount > 0 ? (
          <>
            <div className="nums mt-1 text-xl font-semibold leading-tight text-amber lg:text-2xl">
              {workingCount} order{workingCount === 1 ? "" : "s"}
            </div>
            <div className="nums mt-1 text-[11px] text-dim">{money(waiting, ccy)} waiting</div>
          </>
        ) : (
          <>
            <div className="mt-1 text-xl font-semibold leading-tight text-dim lg:text-2xl">None</div>
            <div className="mt-1 text-[11px] text-faint">nothing resting</div>
          </>
        )}
      </button>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "up" }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-panel-2 p-3 lg:p-4">
      <div className="text-[11px] uppercase tracking-wide text-faint">{label}</div>
      <div className={`nums mt-1 break-words text-xl font-semibold leading-tight lg:text-2xl ${tone === "up" ? "text-up" : "text-ink"}`}>{value}</div>
      <div className="mt-1 text-[11px] text-dim">{sub}</div>
    </div>
  );
}

/* ================================================================== working */

function WorkingStrip({
  entries,
  now,
  fetchedAt,
  live,
  pulse,
  cancel,
  ccy,
  playOf,
}: {
  entries: PresenceEntry<RestingOrder>[];
  now: number;
  fetchedAt: number;
  live: boolean;
  pulse: Set<string>;
  cancel: ReturnType<PortfolioSource["useCancel"]>;
  ccy: Ccy;
  /** router_order_id → the play it's a leg of. */
  playOf: Map<string, Play>;
}) {
  const { format } = useOddsFormat();
  const count = entries.filter((e) => e.phase !== "exit").length;
  return (
    <section className="rounded-lg border border-amber/30 bg-amber/[0.04] p-3" aria-label="Working orders">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-amber">Working</h2>
        <span className="nums text-[11px] text-faint">{count}</span>
        <span
          className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${live ? "bg-up/10 text-up" : "bg-faint/10 text-faint"}`}
          title={live ? "Live — fills stream in from your orders channel" : "Stream offline — checking every 15 s"}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-up motion-safe:animate-pulse" : "bg-faint"}`} />
          {live ? "live" : "polling"}
        </span>
      </div>
      {cancel.error && <div className="mb-2 text-[12px] text-down">{cancel.error}</div>}
      <ul className="flex flex-col gap-2">
        {entries.map(({ key, item: o, phase }) => {
          const pct = fillPct(o);
          const total = o.amount ?? (o.filled_amt ?? 0) + (o.unfilled_amt ?? 0);
          const venue = o.partner_name ?? o.partner_id ?? "—";
          const busy = cancel.pendingId === o.router_order_id;
          const play = playOf.get(o.router_order_id);
          return (
            <li
              key={key}
              className={`rounded-md border border-border bg-panel-2 p-3 transition-colors duration-300 motion-reduce:transition-none ${pulse.has(o.router_order_id) ? "bg-amber/10" : ""} ${phaseClass(phase)}`}
            >
              <div className="flex min-w-0 items-start gap-2">
                <VenueLogo name={venue} size={18} />
                <div className="min-w-0 flex-1">
                  <div className="break-words text-[13px] text-ink">{o.title ?? "—"}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-faint">
                    {play && <PlayChip play={play} />}
                    <span>
                      {venue} · waited <span className="nums">{fmtWaited(waitedSeconds(o, now, fetchedAt))}</span>
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busy || phase === "exit"}
                  onClick={() => cancel.cancel(o.router_order_id)}
                  className="-my-1 min-h-11 shrink-0 rounded-full border border-border-2 px-4 text-[12px] text-dim hover:border-down/50 hover:text-down disabled:opacity-40 lg:min-h-8"
                >
                  {busy ? "Cancelling…" : "Cancel"}
                </button>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div
                  className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-border"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(pct)}
                  aria-label="Filled"
                >
                  <div className="gb-bar h-full rounded-full bg-up" style={{ width: `${pct}%` }} />
                </div>
                <span className="nums shrink-0 text-[11px] text-dim">
                  {money(o.filled_amt ?? 0, ccy)} / {money(total, ccy)}
                </span>
                <span className="nums shrink-0 text-[12px] text-ink">@ {fmtPrice(o.price, null, format)}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ================================================================ game card */

function GameCard({
  source,
  game,
  contest,
  leagueOf,
  state,
  now,
  ccy,
  allowed,
  root,
  streamed,
  isMobile,
  activeKey,
  plays,
  onVisible,
  onValue,
  onPick,
}: {
  source: PortfolioSource;
  game: GameHolding;
  contest: Contest | undefined;
  leagueOf: (id: string | undefined) => League | undefined;
  state: StateEntry | undefined;
  now: number;
  ccy: Ccy;
  allowed: ReadonlySet<string> | null;
  root: Element | null;
  streamed: boolean;
  isMobile: boolean;
  activeKey: string | null;
  plays: Map<string, Play>;
  onVisible: (id: string, v: boolean) => void;
  onValue: (id: string, v: ValueSummary) => void;
  onPick: (p: TicketPick) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { visible } = useNearViewport(ref, root);
  const id = game.contestId;
  useEffect(() => {
    onVisible(id, visible);
  }, [id, visible, onVisible]);
  useEffect(() => () => onVisible(id, false), [id, onVisible]);

  const hashes = useMemo(() => game.rows.map((r) => r.positionHash).filter((h): h is string => !!h), [game.rows]);
  const realGame = id !== NO_CONTEST;
  // Every held game loads its prices up front (the shared 4-at-a-time queue), not only once its
  // card nears the screen: the summary's Value now needs them all, and through the compressed
  // public feed a game is ~100–150 KB, not the API's 3.3 MB. Polling stays visible-only.
  const q = source.useGameLiquidity(id, contest ? isGame(contest) : true, hashes, {
    enabled: realGame,
    poll: visible && !streamed,
  });
  const prices = useMemo(() => {
    const m = new Map<string, RowPrice>();
    for (const r of game.rows) m.set(r.key, q.isError && !q.data ? { state: "none" } : rowPrice(r, q.data, allowed));
    return m;
  }, [game.rows, q.data, q.isError, allowed]);
  const value = useMemo(() => rowsValue(game.rows, (r) => prices.get(r.key) ?? { state: "pending" }), [game.rows, prices]);
  useEffect(() => onValue(id, value), [id, value, onValue]);

  const [open, setOpen] = useState(false);
  const expanded = !isMobile || open;
  const live = isLiveGame(contest, liveFromState(state), now);
  const league = leagueOf(contest?.league_id);
  const title = realGame ? matchupOf(contest, game.rows[0]?.title ?? "…") : "Unattributed orders";
  const href = realGame ? `/contests/${encodeURIComponent(id)}` : null;
  const change = value.value - value.cost;
  const rowEntries = usePresence(game.rows, (r) => r.key, true);
  // This game's plays (exposed first), each with its rows; the rest stay plain position rows.
  const gamePlays = useMemo(() => {
    const ids = [...new Set(game.rows.map((r) => r.playId).filter((x): x is string => !!x))];
    return ids
      .map((pid) => plays.get(pid))
      .filter((p): p is Play => !!p)
      .sort((a, b) => Number(b.exposed) - Number(a.exposed));
  }, [game.rows, plays]);
  const anyExposed = gamePlays.some((p) => p.exposed);
  const positionRow = (row: HoldingRow) => (
    <PositionRow
      source={source}
      row={row}
      price={prices.get(row.key) ?? { state: "pending" }}
      data={q.data}
      ccy={ccy}
      allowed={allowed}
      href={href}
      activeKey={activeKey}
      onPick={onPick}
      onRetry={q.isError ? q.refetch : undefined}
    />
  );

  const header = (
    <>
      <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-faint">
        {league && <VenueLogo name={league.name} logoUrl={(league as { image_url?: string | null }).image_url} size={14} />}
        <span className="shrink-0 font-medium uppercase tracking-wide text-dim">{league?.name ?? (contest ? "" : "…")}</span>
        <span className="min-w-0 truncate">
          <StartOrClock contest={contest} live={live} state={state} />
        </span>
      </div>
      <div className="mt-1 flex min-w-0 items-start gap-3">
        <div className="min-w-0 flex-1">
          {contest || !realGame ? (
            <div className="break-words text-[14px] font-medium leading-snug text-ink">
              {title}
              {anyExposed && (
                <span className="ml-2 inline-flex translate-y-[-1px] items-center rounded-full border border-down/50 bg-down/15 px-1.5 py-px align-middle text-[10px] font-semibold uppercase tracking-wide text-down">
                  Exposed
                </span>
              )}
            </div>
          ) : (
            <Skeleton className="mt-0.5 h-4 w-48 max-w-full" />
          )}
          {live && scoresOf(state) && contest && teamsOf(contest) && (
            <div className="nums mt-0.5 text-[12px] font-semibold text-blue">
              {scoresOf(state)!.away ?? "–"} – {scoresOf(state)!.home ?? "–"}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="nums text-[13px] text-ink">{money(game.stake, ccy)}</div>
          <div className="nums text-[11px] text-up">→ {money(game.payout, ccy)}</div>
        </div>
        {isMobile && (
          <span className="flex shrink-0 flex-col items-end gap-0.5 pt-0.5">
            <span className={`nums text-[12px] ${value.priced > 0 ? toneOf(change) : "text-faint"}`} aria-label="Value now vs cost">
              {value.priced > 0 ? arrowOf(change) || "•" : "…"}
            </span>
            <span className={`text-[11px] text-faint transition-transform duration-150 motion-reduce:transition-none ${open ? "rotate-90" : ""}`} aria-hidden>
              ▸
            </span>
          </span>
        )}
      </div>
    </>
  );

  return (
    <div ref={ref} data-contest={id} className="min-w-0">
      <SoftCard highlight={live} className="p-3">
        {isMobile ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="-m-3 block w-[calc(100%+1.5rem)] rounded-lg p-3 text-left"
          >
            {header}
          </button>
        ) : (
          header
        )}
        {!isMobile && (
          <div className="mt-1 flex justify-end text-[11px] text-faint">
            {value.priced > 0 ? (
              <span className="nums">
                now {money(value.value, ccy)}{" "}
                <span className={toneOf(change)}>
                  {arrowOf(change)} {signedMoney(change, ccy)}
                </span>
                {value.priced < value.total ? ` · ${value.priced}/${value.total} priced` : ""}
              </span>
            ) : value.pending > 0 ? (
              <Skeleton className="h-3 w-28" />
            ) : null}
          </div>
        )}
        {expanded && (
          <div className="mt-3 flex flex-col gap-2">
            {gamePlays.map((p) => (
              <PlayBlock key={p.id} play={p} ccy={ccy}>
                {rowEntries
                  .filter((e) => e.item.playId === p.id)
                  .map((e) => (
                    <div key={e.key} className={phaseClass(e.phase)}>
                      {positionRow(e.item)}
                    </div>
                  ))}
              </PlayBlock>
            ))}
            {rowEntries
              .filter((e) => !e.item.playId || !plays.has(e.item.playId))
              .map((e) => (
                <div key={e.key} className={phaseClass(e.phase)}>
                  {positionRow(e.item)}
                </div>
              ))}
          </div>
        )}
      </SoftCard>
    </div>
  );
}

/* ==================================================================== plays */

/** "Arb" / "Market making" / "Hedged" / "Play" — red when the play is exposed. Market making links to its card. */
function PlayChip({ play }: { play: Play }) {
  const cls = `inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${
    play.exposed ? "border-down/50 bg-down/15 text-down" : "border-blue/40 bg-blue/10 text-blue"
  }`;
  const label = `${PLAY_LABEL[play.kind]}${play.exposed ? " · exposed" : ""}`;
  return play.kind === "mm" ? (
    <Link to={mmFocusHref(play.id)} className={`${cls} hover:underline`} title="Open its Market making card">
      {label} →
    </Link>
  ) : (
    <span className={cls} title={`Part of a ${PLAY_LABEL[play.kind].toLowerCase()} (opportunity ${play.id})`}>
      {label}
    </span>
  );
}

/**
 * One play inside a game card: kind chip, the payout per outcome, then its legs — the filled
 * ones as position rows (children), resting and unfilled ones as one-line legs. Red when exposed.
 */
function PlayBlock({ play, ccy, children }: { play: Play; ccy: Ccy; children: ReactNode }) {
  const { format } = useOddsFormat();
  const restingLegs = play.legs.filter((l) => legState(l) === "resting");
  const dead = play.legs.filter((l) => legState(l) === "dead");
  // Exposed with an outcome that holds nothing: name it ("Heat win · nothing filled").
  const bare = play.exposed ? (play.outcomes ?? []).filter((o) => o.key !== "otherwise" && o.contracts <= 0) : [];
  return (
    <section
      aria-label={`${PLAY_LABEL[play.kind]} play`}
      className={`rounded-lg border p-2 ${play.exposed ? "border-down/50 bg-down/[0.06]" : "border-border-2/70 bg-panel-2"}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pb-2">
        <PlayChip play={play} />
        {play.outcomes ? (
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-[12px]">
            {play.outcomes.map((o, i) => (
              <span key={o.key} className="whitespace-nowrap">
                {i > 0 && <span className="mr-2 text-faint">·</span>}
                <span className="text-faint">if {o.label}</span> <span className={`nums font-semibold ${toneOf(o.payout)}`}>{signedMoney(o.payout, ccy)}</span>
              </span>
            ))}
          </span>
        ) : (
          <span className="nums text-[12px] text-faint">cost {money(play.cost, ccy)}</span>
        )}
        <span className="nums ml-auto text-[11px] text-faint">{play.legs.length} legs</span>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
      {(restingLegs.length > 0 || bare.length > 0 || dead.length > 0) && (
        <ul className="mt-2 flex flex-col gap-1 px-1 text-[12px]">
          {restingLegs.map((l) => (
            <li key={l.router_order_id} className="flex min-w-0 items-center gap-2">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-dim" title={l.title ?? undefined}>
                {outcomeLabel(l.title)} <span className="text-faint">· {l.partner?.partner_name ?? l.partner_id ?? "—"}</span>
              </span>
              <span className="nums shrink-0 text-amber">
                working {money(waitingAmt(l), ccy)} @ {fmtPrice(l.price, null, format)}
              </span>
            </li>
          ))}
          {bare
            .filter((o) => !restingLegs.some((l) => outcomeLabel(l.title) === o.label))
            .map((o) => (
              <li key={o.key} className="flex min-w-0 items-center gap-2 text-down">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-down" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                <span className="shrink-0">nothing filled</span>
              </li>
            ))}
          {dead.length > 0 && (
            <li className="text-[11px] text-faint">
              {dead.length} leg{dead.length === 1 ? "" : "s"} cancelled or unfilled
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function StartOrClock({ contest, live, state }: { contest: Contest | undefined; live: boolean; state?: StateEntry }) {
  if (!contest) return null;
  if (live) {
    const text = [state?.state?.period?.label, state?.state?.clock?.display].filter(Boolean).join(" · ");
    return <span className="nums font-medium text-blue">· {text || "Live"}</span>;
  }
  if (!isGame(contest)) return null;
  const start = startMs(contest);
  if (!Number.isFinite(start)) return null;
  const soon = start > Date.now() && start - Date.now() < 3 * 3_600_000;
  return <span className="nums">· {soon ? `${fmtRelative(contest.starts_at)} · ` : ""}{fmtKickoff(contest.starts_at)}</span>;
}

/* ============================================================= position row */

function PositionRow({
  source,
  row,
  price,
  data,
  ccy,
  allowed,
  href,
  activeKey,
  onPick,
  onRetry,
}: {
  source: PortfolioSource;
  row: HoldingRow;
  price: RowPrice;
  data: { positions: LiquidityPosition[] } | undefined;
  ccy: Ccy;
  allowed: ReadonlySet<string> | null;
  href: string | null;
  activeKey: string | null;
  onPick: (p: TicketPick) => void;
  onRetry?: () => void;
}) {
  const { format } = useOddsFormat();
  const [details, setDetails] = useState(false);

  // Add more: the same side at the venue you hold it on (paper fills book the practice book,
  // which isn't a venue you can pick — so paper takes the side's best venue, like Markets).
  const add = useMemo(() => {
    if (price.state !== "priced") return null;
    const pos = price.position as LiquidityPosition;
    const own = (pos.partner_liquidities ?? []).find(
      (pl) =>
        pl.partner_id === row.partnerId &&
        (pl as { actionable?: boolean }).actionable !== false &&
        (pl.price ?? 0) > 0 &&
        (pl.available ?? 0) > 0,
    );
    const pl = (own ?? (row.internalBook ? price.quote : null)) as PartnerLiquidity | null;
    return pl ? { pos, pl } : null;
  }, [price, row.partnerId, row.internalBook]);

  const hedge = useMemo(() => {
    if (!data || !row.positionHash) return null;
    const c = findComplement(data.positions, row.positionHash, allowed);
    if (!c) return null;
    if (!c.best || typeof c.best.price !== "number") return { pos: c.position as LiquidityPosition, pl: null, stake: 0, label: oppositeLabel(c.position as LiquidityPosition) };
    const stake = hedgeStake(row.contracts, c.best.price, ccy);
    return { pos: c.position as LiquidityPosition, pl: c.best as PartnerLiquidity, stake, label: oppositeLabel(c.position as LiquidityPosition) };
  }, [data, row.positionHash, row.contracts, allowed, ccy]);

  const addKey = `add|${row.contestId}|${row.key}`;
  const hedgeKey = `hedge|${row.contestId}|${row.key}`;
  const hedgeHint = hedge?.pl
    ? `Stake ${money(hedge.stake, ccy)} on ${hedge.label} to lock in about ${money(row.contracts, ccy)} either way`
    : null;
  const lockedNet = hedge?.pl ? row.contracts - row.stake - hedge.stake : null;

  const pending = price.state === "pending";
  const btn =
    "relative inline-flex min-h-11 items-center justify-center rounded-full border px-3.5 text-[12px] font-medium transition-colors duration-150 motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-40 lg:min-h-8";
  const idle = "border-border-2 bg-panel text-dim enabled:hover:border-amber/40 enabled:hover:text-ink";
  const on = "border-amber/70 bg-amber/10 text-amber";

  return (
    <div className="rounded-md border border-border/70 bg-panel p-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-[10rem] flex-1">
          <div className="break-words text-[14px] text-ink" title={row.title}>
            {row.label}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-faint">
            <VenueLogo name={row.venueName} logoUrl={row.venueLogo} size={14} />
            <span className="min-w-0 truncate">{row.internalBook ? `${row.venueName} · paper` : row.venueName}</span>
          </div>
        </div>
        <dl className="grid w-full grid-cols-3 gap-3 text-right sm:w-auto sm:min-w-[18rem]">
          <Cell label="Contracts">
            <span className="nums">{row.contracts.toLocaleString(undefined, { maximumFractionDigits: row.contracts < 100 ? 2 : 0 })}</span>
          </Cell>
          <Cell label="Avg price">
            <span className="nums">{fmtPrice(row.avgPrice, null, format)}</span>
          </Cell>
          <Cell label="Now">
            {pending ? (
              <Skeleton className="ml-auto h-4 w-12" />
            ) : price.state === "priced" ? (
              <FlashText value={price.price} className="justify-end">
                <span className="nums">{fmtPrice(price.price, price.quote.american_odds, format)}</span>
                {arrowOf(price.price - row.avgPrice) && (
                  <span className={`ml-1 text-[11px] ${toneOf(price.price - row.avgPrice)}`}>{arrowOf(price.price - row.avgPrice)}</span>
                )}
              </FlashText>
            ) : onRetry ? (
              <button type="button" onClick={onRetry} className="text-[12px] text-amber hover:underline">
                Retry
              </button>
            ) : (
              <span className="text-faint" title="No venue in scope is quoting this side right now">
                —
              </span>
            )}
          </Cell>
        </dl>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <button
          type="button"
          disabled={!add}
          aria-pressed={activeKey === addKey}
          title={add ? `Buy more ${row.label} at ${add.pl.partner_name ?? add.pl.partner_id}` : pending ? "Loading prices…" : `No price at ${row.venueName} right now`}
          onClick={() => add && onPick({ key: addKey, kind: "add", contestId: row.contestId, position: add.pos, pl: add.pl })}
          className={`${btn} ${activeKey === addKey ? on : idle}`}
        >
          Add more
        </button>
        {hedge ? (
          <button
            type="button"
            disabled={!hedge.pl || hedge.stake <= 0}
            aria-pressed={activeKey === hedgeKey}
            title={
              hedgeHint
                ? `${hedgeHint}${lockedNet != null ? ` (${signedMoney(lockedNet, ccy)} net, before fees)` : ""}`
                : `No price on ${hedge.label} right now`
            }
            onClick={() =>
              hedge.pl &&
              onPick({
                key: hedgeKey,
                kind: "hedge",
                contestId: row.contestId,
                position: hedge.pos,
                pl: hedge.pl,
                initialStake: hedge.stake,
                ...latestOpportunity(row.orders),
                hint: `${hedgeHint}${lockedNet != null ? ` (${signedMoney(lockedNet, ccy)} net, before fees).` : "."}`,
              })
            }
            className={`${btn} ${activeKey === hedgeKey ? on : idle}`}
          >
            Hedge
          </button>
        ) : pending ? (
          <button type="button" disabled className={`${btn} ${idle}`}>
            Hedge
          </button>
        ) : null}
        {href && (
          <Link to={href} className={`${btn} ${idle} hover:border-amber/40 hover:text-ink`}>
            Game page
          </Link>
        )}
        {row.positionHash && row.contestId !== NO_CONTEST && (
          <Link to={marketHref({ contestId: row.contestId, positionHash: row.positionHash })} className={`${btn} ${idle} hover:border-amber/40 hover:text-ink`}>
            View market
          </Link>
        )}
        <button type="button" aria-expanded={details} onClick={() => setDetails((v) => !v)} className={`${btn} ${idle}`}>
          Order details <span className={`ml-1 text-[10px] transition-transform ${details ? "rotate-90" : ""}`}>▸</span>
        </button>
      </div>

      {details && (
        <ul className="gb-fade mt-3 flex flex-col divide-y divide-border/60 rounded-md border border-border/60 bg-bg">
          {row.orders.map((o) => (
            <OrderLine key={o.router_order_id} source={source} order={o} ccy={ccy} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wide text-faint">{label}</dt>
      <dd className="mt-0.5 text-[14px] text-ink">{children}</dd>
    </div>
  );
}

/** One order behind a row: time, fill, ids — and its resolution on demand. */
function OrderLine({ source, order: o, ccy }: { source: PortfolioSource; order: OrderLike; ccy: Ccy }) {
  const { format } = useOddsFormat();
  const [open, setOpen] = useState(false);
  const filled = o.filled_amt ?? o.amount ?? 0;
  const partial = (o.fill_status ?? "").toLowerCase() !== "filled";
  return (
    <li className="px-3 py-2 text-[12px]">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex min-h-11 w-full flex-wrap items-center gap-x-3 gap-y-0.5 text-left lg:min-h-0">
        <span className="nums text-dim">{fmtDateTime(o.placed_datetime ?? o.created_at)}</span>
        <span className="nums text-ink">
          {money(filled, ccy)} @ {fmtPrice(o.price, null, format)}
          {partial && <span className="ml-1 text-amber">· {o.fill_status ?? "partial"}</span>}
        </span>
        <span className="ml-auto text-[11px] text-faint">Resolution {open ? "▾" : "▸"}</span>
      </button>
      {open && <ResolutionDetail source={source} order={o} />}
    </li>
  );
}

/* ---------------------------------------------------- resolution (reused) */

function ResolutionDetail({ source, order }: { source: PortfolioSource; order: OrderLike }) {
  const q = source.useResolution(order.router_order_id, true);
  return <ResolutionView order={order} state={q} />;
}

/* ========================================================== recently settled */

function RecentlySettled({
  entries,
  state,
  ccy,
  contestOf,
  moreLeft,
  onMore,
}: {
  entries: PresenceEntry<SettledGame>[];
  state: ListState<Order>;
  ccy: Ccy;
  contestOf: (id: string) => Contest | undefined;
  moreLeft: boolean;
  onMore: () => void;
}) {
  const historyLink = (
    <Link to="/history" className="inline-flex min-h-11 items-center text-[12px] text-dim hover:text-amber lg:min-h-0">
      All history →
    </Link>
  );
  return (
    <section aria-label="Recently settled" className="mt-2">
      <SectionHeading title="Recently settled" right={<span className="text-[11px] text-faint">last 7 days</span>} />
      <div className="mt-2 flex flex-col gap-2">
        {state.isLoading ? (
          [0, 1].map((i) => (
            <div key={i} className="rounded-lg border border-border bg-panel-2 p-3">
              <Skeleton className="h-4 w-48 max-w-full" />
              <Skeleton className="mt-2 h-3 w-64 max-w-full" />
            </div>
          ))
        ) : state.isError ? (
          <ErrorState error={state.error} onRetry={state.refetch} />
        ) : entries.length === 0 ? (
          <div className="rounded-lg border border-border bg-panel-2 p-3 text-[13px] text-dim">Nothing settled in the last 7 days.</div>
        ) : (
          entries.map(({ key, item: g, phase }) => {
            const c = g.contestId === NO_CONTEST ? undefined : contestOf(g.contestId);
            return (
              <div key={key} className={phaseClass(phase)}>
                <SoftCard className="p-3">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <div className="min-w-0 flex-1 break-words text-[13px] font-medium text-ink">
                      {g.contestId === NO_CONTEST ? "Unattributed orders" : c ? matchupOf(c, "…") : <Skeleton className="h-4 w-40 max-w-full" />}
                    </div>
                    <span className="nums shrink-0 text-[11px] text-faint">{fmtAge(new Date(g.settledAt).toISOString())}</span>
                  </div>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {g.rows.map((r) => (
                      <li key={r.key} className="flex min-w-0 items-center gap-2 text-[13px]">
                        <ResultPill result={r.result} />
                        <span className="min-w-0 flex-1 truncate text-dim" title={`${r.title} · ${r.venueName}`}>
                          {r.label} <span className="text-faint">· {r.venueName}</span>
                        </span>
                        <span className={`nums shrink-0 ${r.result === "push" ? "text-dim" : toneOf(r.amount)}`}>
                          {r.result === "push" ? `${money(r.stake, ccy)} back` : signedMoney(r.amount, ccy)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </SoftCard>
              </div>
            );
          })
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {(moreLeft || state.hasMore) && !state.isLoading && (
          <button
            type="button"
            onClick={onMore}
            disabled={state.loadingMore}
            className="min-h-11 rounded-full border border-border-2 bg-panel-2 px-4 text-[13px] text-dim hover:bg-raised hover:text-ink disabled:opacity-50 lg:min-h-9"
          >
            {state.loadingMore ? "Loading…" : "Show more"}
          </button>
        )}
        <span className="ml-auto">{historyLink}</span>
      </div>
    </section>
  );
}

function ResultPill({ result }: { result: SettledGame["rows"][number]["result"] }) {
  const cfg =
    result === "win"
      ? { t: "Won", c: "border-up/40 bg-up/10 text-up" }
      : result === "lose"
        ? { t: "Lost", c: "border-down/40 bg-down/10 text-down" }
        : result === "push"
          ? { t: "Push", c: "border-border-2 bg-panel text-dim" }
          : { t: "Mixed", c: "border-amber/40 bg-amber/10 text-amber" };
  return <span className={`inline-flex w-12 shrink-0 justify-center rounded-full border py-0.5 text-[11px] font-medium ${cfg.c}`}>{cfg.t}</span>;
}

/* =============================================================== empty, etc */

/** Where a user connects or funds a venue (greenbook doesn't manage venue accounts). */
const VENUE_ACCOUNTS_URL = "https://app.openmarkets.ai";

function EmptyNudge({ ccy, noVenue }: { ccy: Ccy; noVenue: boolean }) {
  const primary =
    "inline-flex min-h-11 items-center rounded-full border border-amber/60 bg-amber/15 px-5 text-[13px] font-semibold text-amber hover:bg-amber/25";
  const secondary =
    "inline-flex min-h-11 items-center rounded-full border border-border-2 bg-panel px-5 text-[13px] text-ink hover:bg-raised";
  return (
    <div className="rounded-lg border border-border bg-panel-2 p-6 text-center">
      <div className="text-[14px] text-ink">{ccy === "ATLAS" ? "No paper positions yet — every venue, play money" : "No real-money positions"}</div>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <Link to="/markets" className={primary}>
          Browse markets
        </Link>
        {ccy === "USD" && noVenue && (
          <a href={VENUE_ACCOUNTS_URL} target="_blank" rel="noreferrer" className={secondary}>
            Connect a venue ↗
          </a>
        )}
      </div>
    </div>
  );
}

function GamesSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading positions">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-lg border border-border bg-panel-2 p-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3.5 w-3.5 rounded-full" />
            <Skeleton className="h-3 w-28" />
          </div>
          <div className="mt-2 flex items-start gap-3">
            <Skeleton className="h-4 w-56 max-w-[60%]" />
            <Skeleton className="ml-auto h-8 w-16" />
          </div>
          <div className="mt-3 hidden rounded-md border border-border/60 p-3 lg:block">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-3 h-8 w-72" />
          </div>
        </div>
      ))}
    </div>
  );
}
